import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import Header from '@/components/Header';
import ProductSearch from '@/components/ProductSearch';
import EditItemModal from '@/components/EditItemModal';
import ShareListModal from '@/components/ShareListModal';
import InvitationsModal from '@/components/InvitationsModal';
import GroupSortModal from '@/components/GroupSortModal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Trash2, Pencil, ExternalLink, ShoppingCart, ShoppingBasket } from 'lucide-react';
import { toast } from 'sonner';

const APP_ICON = "https://static.prod-images.emergentagent.com/jobs/3c404c7b-5045-4d45-8ddd-9111c2fbc8c7/images/1edd49db85dba9cc04f46bd52929381e881d2b7dac026dd5c68dd6bec34e756c.png";

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const [lists, setLists] = useState([]);
  const [activeListId, setActiveListId] = useState(null);
  const [items, setItems] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [showInvitations, setShowInvitations] = useState(false);
  const [showGroupSort, setShowGroupSort] = useState(false);
  const [groupOrder, setGroupOrder] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  const activeList = lists.find(l => l.id === activeListId);
  const canWrite = activeList ? (activeList.permission === 'owner' || activeList.permission === 'write') : false;
  const isOwner = activeList?.is_owner || false;

  const fetchLists = useCallback(async () => {
    try {
      const { data } = await api.get('/lists');
      setLists(data);
      if (data.length > 0 && !activeListId) {
        const preferred = user?.active_list_id;
        const found = data.find(l => l.id === preferred);
        setActiveListId(found ? found.id : data[0].id);
      }
    } catch { /* ignore */ }
  }, [user?.active_list_id, activeListId]);

  const fetchItems = useCallback(async () => {
    if (!activeListId) return;
    setLoadingItems(true);
    try {
      const { data } = await api.get(`/lists/${activeListId}/items`);
      setItems(data);
    } catch { setItems([]); }
    finally { setLoadingItems(false); }
  }, [activeListId]);

  const fetchInvitations = useCallback(async () => {
    try {
      const { data } = await api.get('/invitations');
      setInvitations(data);
    } catch { /* ignore */ }
  }, []);

  const fetchGroupOrder = useCallback(async () => {
    try {
      const { data } = await api.get('/user-group-order');
      setGroupOrder(data.group_ids || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchLists(); fetchInvitations(); fetchGroupOrder(); }, [fetchLists, fetchInvitations, fetchGroupOrder]);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleSelectList = async (listId) => {
    setActiveListId(listId);
    try { await api.put(`/lists/${listId}/select`); } catch { /* ignore */ }
  };

  const handleCreateList = async (name) => {
    try {
      const { data } = await api.post('/lists', { name });
      setLists(prev => [...prev, data]);
      setActiveListId(data.id);
      toast.success('Liste oprettet');
    } catch (err) { toast.error('Kunne ikke oprette liste'); }
  };

  const handleDeleteList = async (listId) => {
    try {
      await api.delete(`/lists/${listId}`);
      setLists(prev => prev.filter(l => l.id !== listId));
      if (activeListId === listId) {
        const defaultList = lists.find(l => l.is_default && l.is_owner);
        setActiveListId(defaultList?.id || lists[0]?.id);
      }
      toast.success('Liste slettet');
    } catch (err) { toast.error(err.response?.data?.detail || 'Kunne ikke slette liste'); }
  };

  const handleLeaveList = async (listId) => {
    try {
      await api.post(`/lists/${listId}/leave`);
      setLists(prev => prev.filter(l => l.id !== listId));
      if (activeListId === listId) {
        const defaultList = lists.find(l => l.is_default && l.is_owner);
        setActiveListId(defaultList?.id || null);
      }
      toast.success('Du har forladt listen');
    } catch { toast.error('Kunne ikke forlade listen'); }
  };

  const handleItemAdded = (item) => {
    setItems(prev => [item, ...prev]);
  };

  const handleToggleItem = async (itemId) => {
    try {
      const { data } = await api.put(`/lists/${activeListId}/items/${itemId}/toggle`);
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, checked: data.checked } : i));
    } catch { toast.error('Kunne ikke opdatere vare'); }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await api.delete(`/lists/${activeListId}/items/${itemId}`);
      setItems(prev => prev.filter(i => i.id !== itemId));
      toast.success('Vare fjernet');
    } catch { toast.error('Kunne ikke fjerne vare'); }
  };

  const handleEditItem = async (itemId, data) => {
    try {
      await api.put(`/lists/${activeListId}/items/${itemId}`, data);
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, ...data } : i));
      setEditingItem(null);
      toast.success('Vare opdateret');
    } catch { toast.error('Kunne ikke opdatere vare'); }
  };

  const handleInvitationAction = async (invId, action) => {
    try {
      await api.post(`/invitations/${invId}/${action}`);
      setInvitations(prev => prev.filter(i => i.id !== invId));
      if (action === 'accept') { fetchLists(); toast.success('Invitation accepteret'); }
      else { toast.success('Invitation afvist'); }
    } catch { toast.error('Kunne ikke behandle invitation'); }
  };

  const handleClearList = async () => {
    if (!activeListId || !window.confirm('Er du sikker på at du vil slette alle varer fra listen?')) return;
    try {
      await api.delete(`/lists/${activeListId}/items`);
      setItems([]);
      toast.success('Listen er ryddet');
    } catch { toast.error('Kunne ikke rydde listen'); }
  };

  // Sort items by group order
  const sortItemsByGroup = useCallback((itemList) => {
    if (!groupOrder.length) return itemList;
    return [...itemList].sort((a, b) => {
      const idxA = a.group_id ? groupOrder.indexOf(a.group_id) : 999;
      const idxB = b.group_id ? groupOrder.indexOf(b.group_id) : 999;
      return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
    });
  }, [groupOrder]);

  const sortedItems = sortItemsByGroup(items);

  // Group items by group_name for section headers
  const groupedItems = useMemo(() => {
    const groups = [];
    let currentGroup = null;
    sortedItems.forEach(item => {
      const gn = item.group_name || 'Øvrige';
      if (gn !== currentGroup) {
        groups.push({ type: 'header', name: gn });
        currentGroup = gn;
      }
      groups.push({ type: 'item', data: item });
    });
    return groups;
  }, [sortedItems]);

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="dashboard-page">
      <Header
        lists={lists} activeListId={activeListId} onSelectList={handleSelectList}
        onCreateList={handleCreateList} onDeleteList={handleDeleteList} onLeaveList={handleLeaveList}
        invitationCount={invitations.length} onShowInvitations={() => setShowInvitations(true)}
        onShowShare={() => setShowShare(true)} onShowGroupSort={() => setShowGroupSort(true)}
        isOwner={isOwner} user={user} onLogout={logout}
      />
      <main className="max-w-3xl mx-auto px-4 pt-4 pb-20">
        {canWrite && activeListId && (
          <ProductSearch activeListId={activeListId} onItemAdded={handleItemAdded} />
        )}
        {loadingItems ? (
          <div className="text-center py-12 text-[#5C706A] font-sans">Indlæser varer...</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16">
            <img src={APP_ICON} className="w-20 h-20 mx-auto mb-4 opacity-40" alt="" />
            <h3 className="text-xl text-[#2F3E3B] mb-2">Listen er tom</h3>
            <p className="text-[#5C706A] font-sans text-sm">
              {canWrite ? 'Brug søgfeltet ovenfor for at tilføje varer' : 'Der er ingen varer på denne liste endnu'}
            </p>
          </div>
        ) : (
          <div className="mt-4">
            {groupedItems.map((entry, idx) =>
              entry.type === 'header' ? (
                <div key={`hdr-${entry.name}-${idx}`} className="mt-5 mb-2 first:mt-0">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.15em] text-[#7F9C82] font-sans pl-1">{entry.name}</h3>
                  <div className="h-px bg-[#7F9C82]/20 mt-1" />
                </div>
              ) : (
                <Card key={entry.data.id}
                  className={`p-3 mb-2 transition-all duration-200 fade-in cursor-pointer ${entry.data.checked ? 'border-[#4A7A4E] bg-[#4A7A4E]' : 'border-[#EAE6DF] hover:border-[#7F9C82]/40'}`}
                  data-testid={`list-item-${entry.data.id}`}
                  onClick={() => canWrite && handleToggleItem(entry.data.id)}>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-sans font-medium ${entry.data.checked ? 'text-white' : 'text-[#2F3E3B]'}`}>{entry.data.product_name}</span>
                        {entry.data.quantity > 1 && <Badge variant="secondary" className={`text-xs ${entry.data.checked ? 'bg-white/20 text-white' : 'bg-[#7F9C82]/10 text-[#7F9C82]'}`}>{entry.data.quantity}x</Badge>}
                      </div>
                      {entry.data.description && <p className={`text-xs font-sans mt-0.5 truncate ${entry.data.checked ? 'text-white/70' : 'text-[#5C706A]'}`}>{entry.data.description}</p>}
                    </div>
                    <div className="flex items-center shrink-0" onClick={e => e.stopPropagation()}>
                      {entry.data.link && (
                        <a href={entry.data.link} target="_blank" rel="noopener noreferrer"
                          className={`p-1.5 rounded-md transition-colors ${entry.data.checked ? 'text-white/70 hover:bg-white/10' : 'hover:bg-[#EAE6DF] text-[#5C706A]'}`}>
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {canWrite && (
                        <>
                          <button onClick={() => handleToggleItem(entry.data.id)}
                            className={`p-1.5 rounded-md transition-colors mr-2 ${entry.data.checked ? 'text-[#FFD700] hover:bg-white/10' : 'text-[#7F9C82] hover:bg-[#7F9C82]/10'}`}
                            title={entry.data.checked ? 'Fjern fra købt' : 'Marker som købt'} data-testid={`cart-item-${entry.data.id}`}>
                            <ShoppingBasket className="w-4 h-4" />
                          </button>
                          {!entry.data.checked && (
                            <button onClick={() => setEditingItem(entry.data)} className="p-1.5 rounded-md hover:bg-[#EAE6DF] text-[#5C706A] transition-colors"
                              data-testid={`edit-item-${entry.data.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                          )}
                          <button onClick={() => handleDeleteItem(entry.data.id)}
                            className={`p-1.5 rounded-md transition-colors ${entry.data.checked ? 'text-white/60 hover:bg-white/10' : 'hover:bg-red-50 text-[#C85547]'}`}
                            data-testid={`delete-item-${entry.data.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              )
            )}
            {items.length > 0 && canWrite && (
              <div className="mt-10 pt-4">
                <button onClick={handleClearList}
                  className="w-full py-3 border-2 border-[#C85547] text-[#C85547] rounded-lg font-sans font-medium hover:bg-[#C85547] hover:text-white transition-colors duration-200"
                  data-testid="clear-list-button">
                  Slet listen
                </button>
              </div>
            )}
          </div>
        )}
      </main>
      {editingItem && <EditItemModal item={editingItem} onSave={handleEditItem} onClose={() => setEditingItem(null)} />}
      {showShare && activeListId && <ShareListModal listId={activeListId} onClose={() => { setShowShare(false); fetchLists(); }} />}
      {showInvitations && <InvitationsModal invitations={invitations} onAction={handleInvitationAction} onClose={() => setShowInvitations(false)} />}
      {showGroupSort && <GroupSortModal onClose={() => { setShowGroupSort(false); fetchGroupOrder(); }} />}
    </div>
  );
}
